import { ApiEntityModel } from '../../_foundation/models/base.models';
import { PtLuxonDate } from './date.models';
import { SetupChangeLogRecord } from '../setup/setup.models';
import { JackingActivityEventCollectionType } from './jacking-activity.models';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';

export class PageArgs {
  page: any;
  options?: any;
  params?: any;
  supportContextKey?: string;
  supportContextLabel?: string;
}

export class OperationFilters {
  openEnded: boolean;
  multiDay: boolean;
  beforeDate: DateTime;
}

export class Stage {
  id: number;
  name: string;
  phaseName: string;
  projectName: string;
}

export class OperationMetadata extends ApiEntityModel<number> {
  projectSpecificOperations: ProjectSpecificOperationSummary[];
  stepCollections: StepCollectionInstance[];
}

export class TimeMaskOptions {
  minDigit: number;
  maxDigit: number;
  digitCombo: number;
  totalMaxDigit: number;
  position: number;
}

export class StageSplit {
  dprSplitOperations: DprSplitOperations[] = [];
  stageId: number;
  stageName: string;
  phaseName: string;
  projectName: string;

  hideOperations: boolean;
}

export class DprSplitOperations {
  operations: ProjectSpecificOperationSummary[] = [];
  dprContract: string;
  visible: boolean = true;
}

export class InitialOperationTimeHelper {
  ptLuxonDate: PtLuxonDate;
  first: boolean;
}

export class LogComment {
  id: number; // api id
  appear: boolean;
  milestone: boolean = false;
  data: string;

  startTimeToShow: string;
  endTimeToShow: string;

  startTimeDate: PtLuxonDate;
  endTimeDate: PtLuxonDate;

  startTimeOffset: number;
  endTimeOffset: number;

  startTimeValid?: boolean;

  recordApiId?: number; // daily log record id
  recordLocalId?: number;

  identifier?: number; // local only
  locked: boolean; // local only to stop users editing comments on other days
}

export class DailyLogOperationInstance {
  id?: number;
  localId?: number;
  responsibilityTypeId?: number;
  projectSpecificOperation: ProjectSpecificOperationSummary;
  projectSpecificOperationId: number;
  cancelledTime: DateTime;
  cancelledTimePtDate: PtLuxonDate;
  cancelledTimeOffset: number;
  pausedTimes: TimeDuration[] = [];
  delayOperationApiIds: number[] = [];
  delayOperationLocalIds: number[] = [];
  delayLinks: DelayAffectedOperationLinkModel[] = [];
  switchTimes: TypeSwitch[] = [];
  newLocationId?: number;
  previousLocationId?: number;
  startConcurrent: boolean;
  endConcurrent: boolean;
  isConcurrentOperation: boolean;
  isInterruption: boolean;
  cancelId: number;
  startTime: PtLuxonDate;
  endTime: PtLuxonDate;
  startTimeToShow: string;
  endTimeToShow: string;
  startTimeOffset: number;
  endTimeOffset: number;
  startConfirmed: boolean = false;
  endConfirmed: boolean = false;

  startComment: LogComment;
  endComment: LogComment;
  comments: LogComment[] = [];
  highestCommentIdentifier: number = 0;

  archived: boolean;

  stepCollectionInstance: StepCollectionInstance;

  jackingActivityId: number;
  diffInMins: number;

  delayLinkTypeId?: DelayLinkType;

  startDisabledForLockedDay: boolean;
  endDisabledForLockedDay: boolean;

  typeSwitchIdsToDelete: number[] = [];

  transitId: number;
  // used locally in transit-preview
  currentlyEditing: boolean;
  addingToTransit: boolean;
  removingFromTransit: boolean;
  createJackingFormInstance: boolean;
  isArrivalOperation: boolean;
}

export enum DelayLinkType {
  Continued = 1,
  Paused = 2,
  Switched = 3,
  Cancelled = 4
}

export class ProjectSpecificOperationSummary extends ApiEntityModel<number> {
  name: string;
  contractualPriority: number;
  dprContractCategoryTag: string;
  stageIds: number[];
  responsibilityRequired: boolean = true;
  responsibilityOptionalStageIds?: number[];
  operationSummary: OperationSummary;

  defaultIsConcurrentOperation: boolean;
  forbidContractual: boolean;

  visible: boolean = true;
  isInterruption: boolean;
}

export class OperationSummary {
  id: number;
  guid: string;
  name: string;
  description: string;
  startDefinition: string;
  endDefinition: string;
  temporaryOperation: boolean;
  constructionProgressLogRelevant: boolean;
  classificationName: string;
  subClassificationName: string;
  stepCollection: StepCollection;
  estimatedDurationSeconds?: number;
  estimatedDurationShow: string;
  projectSpecificOperationIds: number[];
}

export class DailyLogSummaryModel {

  constructor(dailyLogOperationInstance: DailyLogOperationInstance, startOfDay: DateTime, endOfDay: DateTime) {
    this.duration = 0;
    this.concurrentDurationModifier = 0;
    this.projectSpecificOperation = dailyLogOperationInstance.projectSpecificOperation.dprContractCategoryTag;
    this.operationId = dailyLogOperationInstance.projectSpecificOperation.id;
    this.operationName = dailyLogOperationInstance.projectSpecificOperation.name ?
      dailyLogOperationInstance.projectSpecificOperation.name :
      dailyLogOperationInstance.projectSpecificOperation.operationSummary.name;
    this.startTime = dailyLogOperationInstance.startTime.dateTime < startOfDay
      ? new PtLuxonDate(startOfDay)
      : dailyLogOperationInstance.startTime;
    this.endTime = dailyLogOperationInstance.endTime;
    const endOfDayMinusOneMinute = endOfDay.minus({minute: 1});

    // if the user has entered an operation that ends on the last minute of the day (23:59), then SJ have said they want the summary to
    // add the extra minute to make sure that the summary displays durations as xx+1:00 instead of xx:59. Eg. an operation from 20:00 to
    // 23:59 should have a duration of 04:00, not 03:59
    if (dailyLogOperationInstance.endTime && dailyLogOperationInstance.endTime.dateTime
      && dailyLogOperationInstance.endTime.dateTime.toMillis() === endOfDayMinusOneMinute.toMillis()) {
      dailyLogOperationInstance.endTime.dateTime = dailyLogOperationInstance.endTime.dateTime.plus({minute: 1});
    }

    const operationEndOrCancelledTime = dailyLogOperationInstance.cancelledTime
      ? dailyLogOperationInstance.cancelledTime
      : dailyLogOperationInstance.endTime.dateTime;

    // if no end or end is after the end of day, then use end of day instead to calculate duration
    if (!operationEndOrCancelledTime || operationEndOrCancelledTime.toMillis() > endOfDay.toMillis()) {
      this.duration = endOfDay.toMillis() - this.startTime.dateTime.toMillis();
    } else {
      this.duration = operationEndOrCancelledTime.toMillis() - this.startTime.dateTime.toMillis();
    }

    if (dailyLogOperationInstance.pausedTimes && dailyLogOperationInstance.pausedTimes.length > 0) {
      let pauseDuration = 0;
      let switchPauseDuration = 0;

      dailyLogOperationInstance.pausedTimes.forEach(pause => {
        if (pause.startDate.dateTime.toMillis() < startOfDay.toMillis())
          pause.startDate.dateTime = cloneDeep(startOfDay);

        if ((pause.endDate.dateTime === null || pause.endDate.dateTime.toMillis() > endOfDay.toMillis()) && endOfDay.toMillis() > pause.startDate.dateTime.toMillis())
          pause.endDate.dateTime = cloneDeep(endOfDay);
      })

      // pauses that effect the current day, AND current operation
      // the start and end times of a pause will have been capped to the startOfDay/endOfDay in the above loop
      // NOTE: a delay could have been originally added and paused an operation, if the user then edits the
      // delay time to no longer be effecting the operation - it may not have removed it from the operation.pausedTimes
      // although it now does this, if done before a recent change in DelaySaveService, it won't have so we still need this here
      const filteredPauses = dailyLogOperationInstance.pausedTimes
        .filter(e => e.startDate.dateTime.toMillis() >= startOfDay.toMillis()
          && e.startDate.dateTime.toMillis() < endOfDay.toMillis()
          && (operationEndOrCancelledTime === null || e.startDate.dateTime.toMillis() < operationEndOrCancelledTime.toMillis())
          && (e.endDate.dateTime === null
            || (e.endDate.dateTime.toMillis() > startOfDay.toMillis()
            && e.endDate.dateTime.toMillis() > dailyLogOperationInstance.startTime.dateTime.toMillis())));

      for (let i = 0; i < filteredPauses.length; i++) {
        const pauseTime = filteredPauses[i];

        if ((pauseTime.endDate && pauseTime.endDate.dateTime && pauseTime.endDate.dateTime.toMillis() <= startOfDay.toMillis()) // if pause is on past day
          || (pauseTime.startDate && pauseTime.startDate.dateTime && pauseTime.startDate.dateTime.toMillis() >= endOfDay.toMillis())) { // or pause is on future day
          continue;
        }

        let breakPausedLoop = false;
        if (dailyLogOperationInstance.switchTimes && dailyLogOperationInstance.switchTimes.length > 0) {
          for (let j = 0; j < dailyLogOperationInstance.switchTimes.length; j++) {
            const switchStart = dailyLogOperationInstance.switchTimes[j].date;
            const switchEnd = dailyLogOperationInstance.switchTimes[j + 1]
              ? dailyLogOperationInstance.switchTimes[j + 1].date.dateTime
              : dailyLogOperationInstance.endTime && dailyLogOperationInstance.endTime.dateTime
                ? dailyLogOperationInstance.endTime.dateTime
                : endOfDay;

            if (dailyLogOperationInstance.switchTimes[j].isConcurrent
              && pauseTime.startDate.dateTime > switchStart.dateTime
              && pauseTime.endDate && pauseTime.endDate.dateTime && pauseTime.endDate.dateTime < switchEnd) {
              breakPausedLoop = true;
              // break;
            }
          }
        }

        const pauseStart = pauseTime.startDate.dateTime.toMillis() < startOfDay.toMillis()
          ? startOfDay
          : pauseTime.startDate.dateTime;
        const pauseEnd = pauseTime.endDate && pauseTime.endDate.dateTime
          ? pauseTime.endDate.dateTime.toMillis() > endOfDay.toMillis()
            ? endOfDay
            : pauseTime.endDate.dateTime
          : endOfDay;

        if (breakPausedLoop) {
          switchPauseDuration = switchPauseDuration + (pauseEnd.toMillis() - pauseStart.toMillis());
        } else {
          pauseDuration = pauseDuration + (pauseEnd.toMillis() - pauseStart.toMillis());
        }
      }

      this.duration -= pauseDuration;
      this.concurrentDurationModifier -= switchPauseDuration;
    }

    this.hasContractualSwitches = dailyLogOperationInstance.switchTimes && dailyLogOperationInstance.switchTimes.some(e => !e.isConcurrent);

    if (dailyLogOperationInstance.switchTimes && dailyLogOperationInstance.switchTimes.length > 0) {
      let concurrentSwitchDuration = 0;

      for (let i = 0; i < dailyLogOperationInstance.switchTimes.length; i++) {
        // make sure it's smaller than the endOrCancelledTime - although it should always be smaller than the end time, we don't care about
        // any switches if the op is cancelled
        const operationEndOrCancelledTimeOrEndOfDay = operationEndOrCancelledTime || endOfDay;

        // dont care about the switch, if it starts on a future day
        if ((dailyLogOperationInstance.switchTimes[i].date
          && dailyLogOperationInstance.switchTimes[i].date.dateTime
          && dailyLogOperationInstance.switchTimes[i].date.dateTime.toMillis() >= endOfDay.toMillis())) {
          continue;
        }

        if (dailyLogOperationInstance.switchTimes[i].isConcurrent && dailyLogOperationInstance.switchTimes[i].date.dateTime < operationEndOrCancelledTimeOrEndOfDay) {
          // if switch starts before the day start, use day start as switchStart
          const switchStart = dailyLogOperationInstance.switchTimes[i].date.dateTime.toMillis() < startOfDay.toMillis()
            ? startOfDay
            : dailyLogOperationInstance.switchTimes[i].date.dateTime;
          let switchEnd = i + 1 < dailyLogOperationInstance.switchTimes.length
            ? dailyLogOperationInstance.switchTimes[i + 1].date.dateTime || operationEndOrCancelledTime || endOfDay
            : operationEndOrCancelledTime || endOfDay;

          // UNLESS switch also ends before day start, in which case ignore
          if (switchEnd.toMillis() < startOfDay.toMillis()) {
            continue;
          }

          // if switch runs into next day, set switch end to be end of day, we dont care about tomorrow duration
          if (switchEnd > endOfDay) {
            switchEnd = endOfDay;
          }

          concurrentSwitchDuration += switchEnd.toMillis() - switchStart.toMillis();
        }
      }

      this.concurrentSwitchDuration = concurrentSwitchDuration;
    }

    if (dailyLogOperationInstance.isConcurrentOperation || dailyLogOperationInstance.isInterruption) {
      let contractualDuration = 0;

      for (let i = 0; i < dailyLogOperationInstance.switchTimes.length; i++) {
        // dont care about the switch, if it starts on a future day
        if ((dailyLogOperationInstance.switchTimes[i].date
          && dailyLogOperationInstance.switchTimes[i].date.dateTime
          && dailyLogOperationInstance.switchTimes[i].date.dateTime.toMillis() >= endOfDay.toMillis())) {
          continue;
        }

        // make sure it's smaller than the endOrCancelledTime - although it should always be smaller than the end time, we don't care about
        // any switches if the op is cancelled
        const operationEndOrCancelledTimeOrEndOfDay = operationEndOrCancelledTime || endOfDay;

        if (!dailyLogOperationInstance.switchTimes[i].isConcurrent && dailyLogOperationInstance.switchTimes[i].date.dateTime < operationEndOrCancelledTimeOrEndOfDay) {
          // if switch starts before the day start, use day start as switchStart
          const contractualStart = dailyLogOperationInstance.switchTimes[i].date.dateTime.toMillis() < startOfDay.toMillis()
            ? startOfDay
            : dailyLogOperationInstance.switchTimes[i].date.dateTime;
          let contractualEnd = i + 1 < dailyLogOperationInstance.switchTimes.length
            ? dailyLogOperationInstance.switchTimes[i + 1].date.dateTime || operationEndOrCancelledTime || endOfDay
            : operationEndOrCancelledTime || endOfDay;

          // UNLESS switch also ends before day start, in which case ignore
          if (contractualEnd.toMillis() < startOfDay.toMillis()) {
            continue;
          }

          // if switch runs into next day, set switch end to be end of day, we dont care about tomorrow duration
          if (contractualEnd.toMillis() > endOfDay.toMillis()) {
            contractualEnd = endOfDay;
          }

          contractualDuration += contractualEnd.toMillis() - contractualStart.toMillis();
        }
      }

      this.concurrentSwitchDuration = this.duration - contractualDuration;
    }

    this.classificationName = dailyLogOperationInstance.projectSpecificOperation.operationSummary.classificationName;
    this.subClassificationName = dailyLogOperationInstance.projectSpecificOperation.operationSummary.subClassificationName;
    this.isConcurrentOperation = dailyLogOperationInstance.isConcurrentOperation || dailyLogOperationInstance.isInterruption;
  }

  projectSpecificOperation: string;
  operationId: number;
  operationName: string;
  isConcurrentOperation: boolean;
  hasContractualSwitches: boolean;
  startTime: PtLuxonDate;
  endTime: PtLuxonDate;
  duration: number;
  classificationName: string;
  subClassificationName: string;
  concurrentSwitchDuration: number;
  concurrentDurationModifier: number; // used to remove pauses that occur inside switched from concurrent duration, not ideal but no time to rework
}

export class StepCollectionInstance {
  id?: number;
  name: string;
  ordinal: number;
  stepInstances: StepInstance[];
  operationIds: number[];
  stepCollectionId?: number;
}

export class StepCollection extends ApiEntityModel<number> {
  name: string;
  ordinal: number;
  steps: Step[];
  operationIds: number[];
  operationEndDelayedEntryShow: string;
  operationEndDelayedEntrySeconds?: number;
}

export class StepInstance extends ApiEntityModel<number> {
  localId: number;
  number: number;
  group: number;
  comment: LogComment;
  secondsDelay: number;
  secondsSpan: number;
  startTime: PtLuxonDate = new PtLuxonDate();
  endTime: PtLuxonDate = new PtLuxonDate();
  startTimeOffset: number;
  endTimeOffset: number;
  isConcurrent: boolean;
  dailyLogRecordId?: number;
  jackingEventGuid: string;
  significantPoint: boolean;

  // local variables
  dailyLogRecordLocalId?: number;
  startTimeToShow: string;
  endTimeToShow: string;
  sortTime: DateTime;
  exclude: boolean = false;
  startConfirmed: boolean = false;
  endConfirmed: boolean = false;
  step: Step;
  stepId: number;
  canConfirmTimes: boolean;

  startDisabledForLockedDay: boolean;
  endDisabledForLockedDay: boolean;
}

export class Step extends ApiEntityModel<number> {
  name: string;
  defaultNumber: number;
  defaultGroup: number;
  lineTypeName: string;
  description: string;

  defaultSecondsSpan: number; // defaultSecondsSpan
  defaultSecondsDelay: number; // defaultSecondsDelay
  defaultIsConcurrent: boolean;
  isSystemDefault: boolean;
  jackingEventCollectionType: JackingActivityEventCollectionType;
  significantPoint: boolean;

  parentCollectionId: number;
}

export class DailyOperationLogTimeEditor {
  startTime: string;
  endTime: string;
}

export class GapDetector {
  gapFound: boolean;
  finished: boolean;
  currentTime: PtLuxonDate;
  operation?: DailyLogOperationInstance;
}

export enum DailyLogType {
  Step = 1,
  OperationStart = 2,
  OperationEnd = 3,
  LocationChange = 4,
  StageChange = 5,
  PhaseChange = 6,
  ProjectChange = 7,
  Comment = 8,
  VesselMasterChanged = 9,
  Transiting = 10,
  Cancel = 11,
  TypeChangeStart = 12,
  TypeChangeEnd = 13,
  Paused = 14,
  Resumed = 15,
  DateChange = 16,
  Continued = 17,
  TimeZone = 18,
  OtherSetupChange = 19,
  CycleChange = 20
}

export enum OperationType {
  Concurrent = 1,
  Contractual = 2,
  Delay = 3,
  Interruption = 4,
}

export enum ResponsibilityType {
  Client = 1,
  Owner = 2,
}

export class DailyLogEditHelper {
  preventDataChange: boolean;
  dailyLogData: DailyOperationLogRecord[];
}

export class DataForDay {
  records: DailyOperationLogRecord[];
  previousDayData: boolean = false; // used to highlight where Continued record(s) are needed
  nextDayData: boolean = false; // used to highlight where a Date Change record is needed
  continuingOperations?: DailyLogOperationInstance[];
  continuingSteps?: StepInstance[];

  // save these so we don't need to re-calculate them later when creating date change and continue records
  startOfDay: PtLuxonDate;
  endOfDay: PtLuxonDate;
}

export class DailyOperationLogRecord {
  id?: number;
  projectSpecificOperationApiId?: number;
  setupChangeLogApiId?: number;
  guid: string;
  startTime: PtLuxonDate;
  endTime: PtLuxonDate;
  startTimeShow: string;
  endTimeShow: string;
  sortTime: DateTime;
  startTimeConfirmed: boolean;
  endTimeConfirmed: boolean;
  startTimeOffset: number;
  endTimeOffset: number;
  comment: LogComment;
  commentAppearOnDPR: boolean;
  commentId?: number;
  commentIdentifier: number;
  operationDefinition?: string; // used for operation start/end records - will display this in the comments column, along with any comments
  stepInstance: StepInstance;
  stepNumber: number;
  ordinal: number; // for whichever lineType the record is
  uniqueOrdinal: number; // unique number for every record in a day, can be switched to change row orders
  operationName: string;
  // TODO: Get distinct list of this, OperationService
  operationInstanceId: number;
  secondaryGuid: string;
  stageIds: number[];
  operationStartTime: PtLuxonDate;
  operationEndTime?: PtLuxonDate;
  isContractual: boolean;
  contractualConcurrentText: string;
  operationClassification: string;
  isInterruption: boolean;
  lineTypeName: string;
  // typeId is used on the app, type is used all over the api, don't ask me why
  // (tried to refactor this but it caused too many issues, not worth the effort to go through fixing them)
  typeId: DailyLogType;
  type?: string;
  selected: boolean; // styles related rows that get highlighted when clicking a row in the grid
  selectedSwap: boolean; // styles the row that the user clicks to swap
  selectedSwapHighlight: boolean; // styles rows that are available to be swapped
  disabled: boolean; // used for UI when showing which rows cannot be selected
  canConfirmTimes: boolean;
  archived: boolean;
  timeZoneId?: number;
  customSort?: number;

  // local variables
  operationInstanceLocalId?: number;
  localSetupChangeLogRecordId?: number;
  localId?: number;
  oldStartTime?: PtLuxonDate;
  oldEndTime?: PtLuxonDate; // used when editing daily log records, to add the correct amount when changing future step times
  operationStartTimeSort?: DateTime; // need to set this as a date obj instead of PtLuxonDate so we can sort on it easier
}

export class SplitPoint {
  splitTime: Date;
  stepPosition: number;
}

export enum DelayChoice {
  pause = 1,
  cancel = 2,
  switch = 3,
  play = 4,
}
export class DelayAffectedOperationLinkModel {
  delayId: number;
  operationId: number;
  typeId: string;
}

export class DelayAffect {
  instance: DailyLogOperationInstance;
  choice: DelayChoice;
  chosenSteps: StepInstance[];
}

export class TypeSwitch {
  date: PtLuxonDate = new PtLuxonDate(); // start date
  dateOffset: number;
  dateToShow: string;
  fullDateString: string;
  isConcurrent: boolean;
  delayApiId: number;
  id: number;
  guid: string;
  operationInstanceApiId?: number;
  // if a split is added at a time that doesn't make sense, because the op is already that split type at that time
  splitNotNeeded: boolean;
}

export class TypeSwitchGroup {
  editors: TypeSwitch[] = [];
  operationId: number;
}

export class TimeDuration {
  startDate: PtLuxonDate = new PtLuxonDate();
  startDateOffset: number;
  endDate: PtLuxonDate = new PtLuxonDate();
  endDateOffset: number;
  delayApiId: number;
  operationInstanceApiId: number;
  guid: string;
  id: number;
}

// Used in the daily-log-entries service as a workaround for saving daily log records to the api
export class DailyOperationLogRecordHelper {
  records: DailyOperationLogRecord[];
  setupChangeLogRecords: SetupChangeLogRecord[];
  nextId: number;
  nextUniqueOrdinal: number;
}

export enum StepSelectionMethod {
  id = 1,
  stepNumber = 2
}

export class RequiredRecordHelper {
  cancelRecordsRequired: boolean;
  switchTimeRecordsRequired: boolean;
  pauseRecordsRequired: boolean;
  resumeRecordsRequired: boolean;
  transitingRecordRequired: boolean;
}

export class TransitingHelper {
  id: number;
  records: DailyOperationLogRecord[];
  finalUniqueOrdinal: number;
}

export class TypeSwitchOperation {
  affect: DelayAffect;
  switchTimes: TypeSwitch[] = [];
}

export class VoyageLogCreateSelectOptions {
  options = {
    title: 'Create a Voyage Log?',
    mode: 'md'
  };
}

export class VoyageLogEndSelectOptions {
  options = {
    title: 'End current Voyage Log?',
    mode: 'md'
  };
}

export class OperationClassificationRowDetails {
  name: string;
  height: number; // percentage for the height of the row
}



// WEBPACK FOOTER //
// ./src/models/projects/operation.models.ts